<template>
  <footer class="site-footer">
    <div class="footer-top">
      <div class="container">
        <div class="row">
          <div class="col-lg-4 col-md-6 mb-5 mb-lg-0">
            <div class="footer-brand">
              <img src="../assets/tutormed-logo.png" alt="TutorMed Logo" height="40">
              <p class="mt-4">A global online medical community that simplifies complex health concepts for various health personnel worldwide.</p>
            </div>
            <div class="footer-newsletter">
              <h5>Stay Updated</h5>
              <div class="newsletter-form">
                <input 
                  type="email" 
                  v-model="newsletterEmail"
                  placeholder="Your email address" 
                  class="newsletter-input"
                  :disabled="isSubscribing"
                  @keyup.enter="subscribeToNewsletter"
                >
                <button 
                  type="button" 
                  class="newsletter-button"
                  @click="subscribeToNewsletter"
                  :disabled="isSubscribing || !isValidEmail"
                >
                  <i v-if="!isSubscribing" class="bi bi-arrow-right"></i>
                  <i v-else class="bi bi-hourglass-split rotating"></i>
                </button>
              </div>
              
              <!-- Success/Error Messages -->
              <div v-if="subscriptionMessage" class="subscription-message" :class="subscriptionStatus">
                <i :class="subscriptionStatus === 'success' ? 'bi bi-check-circle' : 'bi bi-exclamation-triangle'"></i>
                {{ subscriptionMessage }}
              </div>
            </div>
          </div>
          <div class="col-lg-2 col-md-6 mb-5 mb-lg-0">
            <div class="footer-links-section">
              <h5>Quick Links</h5>
              <ul class="footer-links">
                <li>
                  <router-link to="/">
                    <i class="bi bi-chevron-right"></i> Home
                  </router-link>
                </li>
                <li>
                  <router-link to="/about">
                    <i class="bi bi-chevron-right"></i> About
                  </router-link>
                </li>
                <li>
                  <router-link to="/programs">
                    <i class="bi bi-chevron-right"></i> Programs
                  </router-link>
                </li>
                <li>
                  <router-link to="/register">
                    <i class="bi bi-chevron-right"></i> Register
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 mb-5 mb-lg-0">
            <div class="footer-links-section">
              <h5>Programs</h5>
              <ul class="footer-links">
                <li>
                  <router-link to="/programs/medical-students">
                    <i class="bi bi-chevron-right"></i> Medical Students
                  </router-link>
                </li>
                <li>
                  <router-link to="/programs/doctors">
                    <i class="bi bi-chevron-right"></i> Doctors / House Officers
                  </router-link>
                </li>
                <li>
                  <router-link to="/programs/nurses">
                    <i class="bi bi-chevron-right"></i> Nurses
                  </router-link>
                </li>
                <li>
                  <router-link to="/programs/physician-assistants">
                    <i class="bi bi-chevron-right"></i> Physician Assistants
                  </router-link>
                </li>
                <li>
                  <router-link to="/programs/gmdc">
                    <i class="bi bi-chevron-right"></i> GMDC Preparation
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-3 col-md-6">
            <div class="footer-contact">
              <h5>Contact Us</h5>
              <ul class="contact-info">
                <li>
                  <i class="bi bi-geo-alt"></i>
                  <span>Accra, Ghana</span>
                </li>
                <li>
                  <i class="bi bi-envelope"></i>
                  <a href="mailto:detutormed@gmail.com">detutormed@gmail.com</a>
                </li>
                <li>
                  <i class="bi bi-telephone"></i>
                  <a href="tel:+233532939555">+233 53 293 9555</a>
                </li>
                <li>
                  <i class="bi bi-whatsapp"></i>
                  <a href="https://wa.me/+233532939555">WhatsApp Support</a>
                </li>
                <li>
                  <i class="bi bi-clock"></i>
                  <span>Mon - Sat: 8:00 AM - 6:00 PM GMT</span>
                </li>
              </ul>
              <div class="social-icons">
                <a href="https://facebook.com/tutormed" target="_blank" class="social-icon" aria-label="Facebook">
                  <i class="bi bi-facebook"></i>
                </a>
                <a href="https://twitter.com/tutormed" target="_blank" class="social-icon" aria-label="Twitter">
                  <i class="bi bi-twitter-x"></i>
                </a>
                <a href="https://instagram.com/tutormed_official" target="_blank" class="social-icon" aria-label="Instagram">
                  <i class="bi bi-instagram"></i>
                </a>
                <a href="https://www.youtube.com/channel/tutormed" target="_blank" class="social-icon" aria-label="YouTube">
                  <i class="bi bi-youtube"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="footer-bottom">
      <div class="container">
        <div class="row">
          <div class="col-12 mb-3">
            <div class="legal-disclaimer">
              <h6>Legal Disclaimer</h6>
              <p>TutorMed is NOT a professional institution and is NOT affiliated to any Medical University in Ghana or Overseas. As such, we do NOT issue out any certificate of participation or graduation. Our aim is just to simplify the sciences taught in recognized and accredited local and foreign medical institutions.</p>
            </div>
          </div>
          <div class="col-md-6">
            <p class="copyright">© {{ new Date().getFullYear() }} TutorMed. All rights reserved. | A Global Online Medical Educational Community</p>
          </div>
          <div class="col-md-6">
            <ul class="footer-legal">
              <li><router-link to="/privacy-policy">Privacy Policy</router-link></li>
              <li><router-link to="/app-privacy">App Privacy</router-link></li>
              <li><router-link to="/terms-of-service">Terms of Service</router-link></li>
              <li><router-link to="/cookie-policy">Cookie Policy</router-link></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import api from '../services/api'

export default {
  name: 'SiteFooter',
  data() {
    return {
      newsletterEmail: '',
      isSubscribing: false,
      subscriptionMessage: '',
      subscriptionStatus: ''
    }
  },
  computed: {
    isValidEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return this.newsletterEmail && emailRegex.test(this.newsletterEmail)
    }
  },
  methods: {
    async subscribeToNewsletter() {
      if (!this.isValidEmail || this.isSubscribing) {
        return
      }

      this.isSubscribing = true
      this.subscriptionMessage = ''
      this.subscriptionStatus = ''

      try {
        const response = await api.post('/users/newsletter/subscribe/', {
          email: this.newsletterEmail,
          source: 'website'
        })

        this.subscriptionStatus = 'success'
        this.subscriptionMessage = 'Successfully subscribed! Please check your email for confirmation.'
        this.newsletterEmail = ''

        setTimeout(() => {
          this.subscriptionMessage = ''
          this.subscriptionStatus = ''
        }, 5000)

      } catch (error) {
        this.subscriptionStatus = 'error'
        
        if (error.response?.data?.error) {
          this.subscriptionMessage = error.response.data.error
        } else if (error.response?.status === 400) {
          this.subscriptionMessage = 'Please enter a valid email address.'
        } else {
          this.subscriptionMessage = 'An error occurred. Please try again later.'
        }

        setTimeout(() => {
          this.subscriptionMessage = ''
          this.subscriptionStatus = ''
        }, 5000)
      } finally {
        this.isSubscribing = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.site-footer {
  --footer-bg: #121212;
  --footer-text: rgba(255, 255, 255, 0.8);
  --footer-link: rgba(255, 255, 255, 0.65);
  --footer-link-hover: var(--primary-color);
  --footer-border: rgba(255, 255, 255, 0.1);
  --footer-bottom-bg: rgba(0, 0, 0, 0.2);
  
  background-color: var(--footer-bg);
  color: var(--footer-text);
  
  .footer-top {
    padding: 70px 0 50px;
    
    @media (max-width: 991.98px) {
      padding: 50px 0 30px;
    }
  }
  
  // Footer Branding
  .footer-brand {
    img {
      height: 40px;
      margin-bottom: 15px;
    }
    
    p {
      color: var(--footer-text);
      opacity: 0.8;
      line-height: 1.7;
      margin-bottom: 0;
    }
  }
  
  // Newsletter
  .footer-newsletter {
    margin-top: 30px;
    
    h5 {
      margin-bottom: 15px;
    }
    
    .newsletter-form {
      position: relative;
      display: flex;
      margin-top: 10px;
      
      .newsletter-input {
        flex: 1;
        height: 46px;
        background-color: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: white;
        padding: 0 20px;
        border-radius: 8px 0 0 8px;
        transition: all 0.3s ease;
        
        &:focus {
          outline: none;
          border-color: var(--primary-color);
          background-color: rgba(255, 255, 255, 0.08);
        }
        
        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }
      
      .newsletter-button {
        width: 46px;
        height: 46px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(45deg, #00CEC9, #20e3de);
        border: none;
        color: white;
        border-radius: 0 8px 8px 0;
        transition: all 0.3s ease;
        
        &:hover {
          background: linear-gradient(45deg, #20e3de, #00CEC9);
          i {
            transform: translateX(3px);
          }
        }
        
        i {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .rotating {
          animation: rotate 1s linear infinite;
        }
      }
    }
    
    .subscription-message {
      margin-top: 15px;
      padding: 12px 16px;
      border-radius: 6px;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      
      &.success {
        background-color: rgba(40, 167, 69, 0.1);
        border: 1px solid rgba(40, 167, 69, 0.3);
        color: #28a745;
      }
      
      &.error {
        background-color: rgba(220, 53, 69, 0.1);
        border: 1px solid rgba(220, 53, 69, 0.3);
        color: #dc3545;
      }
      
      i {
        font-size: 16px;
      }
    }
  }
  
  // Footer Headings
  h5 {
    color: white;
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 25px;
    position: relative;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 3px;
      background: linear-gradient(45deg, #00CEC9, #20e3de);
      border-radius: 3px;
    }
  }
  
  // Footer Links
  .footer-links-section {
    .footer-links {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0;
      
      li {
        margin-bottom: 12px;
        
        a {
          color: var(--footer-link);
          display: flex;
          align-items: center;
          transition: all 0.3s ease;
          
          i {
            font-size: 0.8rem;
            margin-right: 8px;
            transition: transform 0.3s ease;
          }
          
          &:hover {
            color: var(--footer-link-hover);
            text-decoration: none;
            transform: translateX(5px);
            
            i {
              transform: translateX(3px);
            }
          }
        }
      }
    }
  }
  
  // Contact Information
  .footer-contact {
    .contact-info {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0;
      
      li {
        margin-bottom: 15px;
        display: flex;
        align-items: flex-start;
        
        i {
          color: var(--primary-color);
          font-size: 1.1rem;
          margin-right: 15px;
          margin-top: 4px;
        }
        
        span, a {
          color: var(--footer-link);
          line-height: 1.6;
          transition: color 0.3s ease;
        }
        
        a:hover {
          color: var(--footer-link-hover);
          text-decoration: none;
        }
      }
    }
    
    .social-icons {
      display: flex;
      gap: 15px;
      margin-top: 25px;
      
      .social-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.05);
        color: var(--footer-link);
        transition: all 0.3s ease;
        
        i {
          font-size: 1.2rem;
        }
        
        &:hover {
          background-color: var(--primary-color);
          color: white;
          transform: translateY(-5px);
        }
      }
    }
  }
  
  // Footer Bottom
  .footer-bottom {
    background-color: var(--footer-bottom-bg);
    padding: 20px 0;
    border-top: 1px solid var(--footer-border);
    
    .copyright {
      color: var(--footer-link);
      font-size: 0.9rem;
      margin-bottom: 0;
      
      @media (max-width: 767.98px) {
        text-align: center;
        margin-bottom: 15px;
      }
    }
    
    .footer-legal {
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
      justify-content: flex-end;
      gap: 20px;
      
      @media (max-width: 767.98px) {
        justify-content: center;
      }
      
      li {
        position: relative;
        
        &:not(:last-child)::after {
          content: '•';
          position: absolute;
          right: -13px;
          color: var(--footer-border);
        }
        
        a {
          color: var(--footer-link);
          font-size: 0.9rem;
          transition: color 0.3s ease;
          
          &:hover {
            color: var(--footer-link-hover);
            text-decoration: none;
          }
        }
      }
    }
    
    .legal-disclaimer {
      padding: 15px;
      border-radius: 8px;
      background-color: rgba(255, 255, 255, 0.05);
      margin-bottom: 20px;
      
      h6 {
        color: white;
        font-weight: 600;
        margin-bottom: 10px;
        font-size: 1rem;
      }
      
      p {
        color: var(--footer-text);
        font-size: 0.9rem;
        line-height: 1.6;
        margin-bottom: 0;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>